import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { Box, Button, ButtonBase, Dialog, DialogActions, DialogTitle, Stack } from '@mui/material'
import { RestartAlt, Search } from '@mui/icons-material'
import { DefaultAccordion } from '../generic/DefaultAccordion'
import Timeline from './Timeline'
import { apiGet } from '../generic/Api_Functions'
import SmartMateriaUITable from './../generic/SmartMateriaUITable'
import SmartSelect from '../generic/smartSelect/SmartSelect'
import { reconcilePlannerFilters } from './plannerFilterOptions'
import SmartSortableContainer from '../generic/smartSortable/SmartSortableContainer'
import { useDndMonitor } from '@dnd-kit/core'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'

import AlertUI from '../generic/AlertUI'
import PlannerDetailsPanel from './detailsTable/PlannerDetailsPanel'
import usePlannerAuthorization from '../generic/hooks/useAuthorization/usePlannerAuthorization'
import usePlannerBlocksLayout from './usePlannerBlocksLayout'
import LoadCapacityBlock from './LoadCapacityBlock'
import MachineDetailDrawer from './MachineDetailDrawer'

// The pill of Monitoring's "Arrange" (`monitoring/byGroup.css`, `.mcx-arrange-pill`), on the theme's colours.
const restoreOrderPillSx = (theme) => ({
  pointerEvents: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px 22px',
  minHeight: '48px',
  borderRadius: '999px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
  transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
  '&:hover': { backgroundColor: 'rgba(122, 224, 169, 0.1)', boxShadow: '0 0 12px rgba(0, 216, 160, 0.25)' },
  '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: '2px' }
})

const emptyPlannerFilters = {
  fabricationOrderId: null,
  partReferenceId: null,
  orderId: null,
  clientId: null
}

const createEmptyPlannerFilters = () => ({ ...emptyPlannerFilters })

// Story 221: the two selectors that follow the plan on screen, emptied.
const withoutPlanOptions = (options) => ({ ...options, fabricationOrders: [], orders: [] })

// Story 175, S90 (DL-5): the short caption of each global filter in the drawer's "Figures filtered by"
// notice, in the order the selectors are laid out.
const FILTER_CAPTION_KEYS = [
  ['fabricationOrderId', 'FO'],
  ['partReferenceId', 'Reference'],
  ['orderId', 'Order'],
  ['clientId', 'Client']
]

const optionLabel = (item) => {
  if (!item) {
    return ''
  }
  if (item.code && item.name) {
    return `${item.code} - ${item.name}`
  }
  return item.code ?? item.name ?? item.fabricationOrder ?? ''
}

const buildOptionsMap = (items = []) => {
  const map = new Map()
  items.forEach((item) => {
    const label = optionLabel(item)
    if (label) {
      map.set(label, item)
    }
  })
  return map
}

// Story 153, CU10/CU11: `SmartSortableContainer` only ever forwards `{ item, index }` to the
// component passed as `renderItem` (see `SmartSortableItem.jsx`), so this cannot be a component
// defined inside `Planner` -- a fresh function identity on every Planner render would make React
// remount it (and the accordion inside it) on every render, which re-fires
// `DefaultAccordion`'s "report my expanded state" effect on every single mount and feeds straight
// back into `usePlannerBlocksLayout`'s state, forever. Keeping this at module scope keeps the
// component identity (and therefore each accordion's own mount) stable across Planner renders; the
// per-render data it needs travels through `PlannerBlockLayoutContext` instead of props.
const PlannerBlockLayoutContext = createContext(null)

// The mouse sensor of `SmartSortableContainer` only starts a drag after 8px of movement, so folding
// the blocks at 4px lands the new layout BEFORE dnd-kit takes its measurements, while still never
// firing on a plain click (which does not move at all).
const PRE_DRAG_DISTANCE = 4

// dnd-kit slides the dropped header into its slot over the sortable's 200ms transition. Unfolding
// under that slide would make the header fly across the re-expanded Gantt, so the fold outlives the
// drop by that much.
const UNFOLD_AFTER_DROP_MS = 250

const hasMovedFarEnough = (origin, event) =>
  Math.abs(event.clientX - origin.x) >= PRE_DRAG_DISTANCE || Math.abs(event.clientY - origin.y) >= PRE_DRAG_DISTANCE

// One Planner block. The HEADER is the only drag handle: a block holds a Gantt whose jobs are dragged,
// filter fields that are typed into and tables that are selected from, and none of that may start a
// reorder. The list runs without dnd-kit's keyboard sensor: Space and Enter on a focused header must
// keep toggling the accordion (and, typed into a filter, must stay a space and a line break).
//
// While a header press is turning into a drag, every block folds -- hidden, NOT collapsed: collapsing
// would travel through `onExpandedStatusChange` and overwrite the saved preference, and
// `DefaultAccordion` unmounts its children when it closes, tearing down and refetching the Gantt.
// Folded, the user drags a short stack of headers instead of crossing 600px of Gantt.
//
// Two things make the fold correct, both measured in a real browser (tests/e2e/plannerBlockDrag):
// - WHEN. dnd-kit measures the items the moment the drag starts, so the fold must already be in the
//   DOM by then: it happens at 4px of movement, before the sensor's 8px, and is committed with
//   `flushSync` because React 18 defers the re-render of a mousemove while the sensor measures
//   synchronously in that same tick.
// - WHERE. Folding the blocks ABOVE the pressed one lifts its header by their height while the pointer
//   stays put (475px measured). The dragged block is therefore drawn by a DragOverlay -- a ghost of
//   its header fixed to the pointer -- and the fold's shift is measured and applied to the ghost as
//   its own offset, so the ghost is born under the pointer wherever the folded stack settled. The
//   stack itself stays compact in its natural place, with the pressed block left in it as a
//   placeholder; a spacer that held the stack around the pointer left a hole the size of the Gantt.
//
// The press is followed on the DOCUMENT from mousedown to mouseup, never on the block: the pointer may
// leave the header before 4px (the fold must still land before dnd-kit's 8px) and may be released
// anywhere after a short move that never became a drag (the blocks must still unfold). A drag that
// did start ends through dnd-kit, whose drop may land with the cursor over any element.
const PlannerBlockAccordion = ({ item, dragHandleProps }) => {
  const {
    blockTitleByKey, isBlockDisabled, renderBlockContent, onBlockExpandedChange,
    isReordering, beginReordering, holdGhostUnderPointer, endReordering
  } = useContext(PlannerBlockLayoutContext)
  const disabled = isBlockDisabled(item.key)
  const wrapperRef = useRef(null)
  // The document listeners of the press in flight, so that whoever ends the press can remove them.
  const pressRef = useRef(null)
  const dragInProgressRef = useRef(false)
  const unfoldTimerRef = useRef(null)

  const releasePress = useCallback(() => {
    const press = pressRef.current
    if (press === null) {
      return
    }
    document.removeEventListener('mousemove', press.onMove)
    document.removeEventListener('mouseup', press.onRelease)
    pressRef.current = null
  }, [])
  useEffect(() => () => {
    releasePress()
    clearTimeout(unfoldTimerRef.current)
  }, [releasePress])

  const foldBlocksAroundPointer = () => {
    const { top: topBeforeFold, width: widthBeforeFold } = wrapperRef.current.getBoundingClientRect()
    // The list is as wide as its widest content (the Gantt), and the fold would shrink it to the
    // window: the browser then clamps any horizontal scroll the user had, and everything jumps
    // sideways under the pointer. Freezing the width in the same commit as the fold prevents it.
    flushSync(() => beginReordering(item.key, widthBeforeFold))
    const shift = topBeforeFold - wrapperRef.current.getBoundingClientRect().top
    holdGhostUnderPointer(Math.max(shift, 0))
  }

  const handleHeaderMouseDown = (event) => {
    releasePress()
    // A new press while the previous drop is still folded: unfold now, so the fold below measures
    // the real layout.
    if (unfoldTimerRef.current !== null) {
      clearTimeout(unfoldTimerRef.current)
      unfoldTimerRef.current = null
      flushSync(() => endReordering())
    }
    const origin = { x: event.clientX, y: event.clientY }
    const onMove = (moveEvent) => {
      if (!hasMovedFarEnough(origin, moveEvent)) {
        return
      }
      document.removeEventListener('mousemove', onMove)
      foldBlocksAroundPointer()
    }
    const onRelease = () => {
      releasePress()
      if (!dragInProgressRef.current) {
        endReordering()
      }
    }
    pressRef.current = { onMove, onRelease }
    // Registered BEFORE dnd-kit's own document listeners (attached by the sensor in the handler called
    // next), so on every mousemove the fold runs first and the sensor measures the folded layout.
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onRelease)
    dragHandleProps.onMouseDown?.(event)
  }

  const finishReordering = () => {
    dragInProgressRef.current = false
    releasePress()
    clearTimeout(unfoldTimerRef.current)
    unfoldTimerRef.current = setTimeout(() => {
      unfoldTimerRef.current = null
      endReordering()
    }, UNFOLD_AFTER_DROP_MS)
  }
  const cancelReordering = () => {
    dragInProgressRef.current = false
    releasePress()
    endReordering()
  }
  useDndMonitor({
    onDragStart: () => { dragInProgressRef.current = true },
    onDragEnd: finishReordering,
    onDragCancel: cancelReordering
  })

  // S111: `DefaultAccordion` forces itself collapsed while `disabled` and reports that forced
  // collapse through the same callback as a real user toggle. Without this guard, a block hidden by
  // a missing permission would silently overwrite its own saved "show" with `false` on every mount,
  // and come back collapsed once the permission is granted again -- reordering must not be able to
  // change what the permission itself does not allow.
  const handleExpandedStatusChange = (expanded) => {
    if (disabled) {
      return
    }
    onBlockExpandedChange(item.key, expanded)
  }
  return (
    <Box
      ref={wrapperRef}
      data-testid={'planner-block-' + item.key}
      // The details panel keeps its 2em padding when only its content is hidden, and that padding
      // showed as an empty strip under every header that had been expanded.
      sx={{ '& .MuiAccordionDetails-root': isReordering ? { display: 'none' } : undefined }}
    >
      <DefaultAccordion
        key={item.key}
        title={blockTitleByKey[item.key]}
        disabled={disabled}
        defaultExpanded={item.expanded}
        onExpandedStatusChange={handleExpandedStatusChange}
        headerSx={{ cursor: isReordering ? 'grabbing' : 'grab', userSelect: 'none' }}
        summaryProps={{ ...dragHandleProps, onMouseDown: handleHeaderMouseDown }}
      >
        <div style={{ display: isReordering ? 'none' : undefined }} data-testid={'planner-block-body-' + item.key}>
          {/* Story 175, S138: `DefaultAccordion` collapses a disabled block in an effect, one commit AFTER
              mounting it when its saved state is expanded -- enough for the details table to fire its first
              requests without the permission. A disabled block paints no content at all. */}
          {disabled ? <></> : renderBlockContent(item.key)}
        </div>
      </DefaultAccordion>
    </Box>
  )
}

const Planner = () => {
  /**
   * @typedef {import("../generic/utilities/objectStructures/planner/planification").Planification} Planification Planification object
   */
  /**
   * @typedef {import("../generic/utilities/objectStructures/planner/eventInfo").ExtendedProps} ExtendedProps EventExtendedProps object
   */

  /** @type {[Planification, React.Dispatch<React.SetStateAction<string[]>>]} */
  const [selectedFile, setSelectedFile] = useState({})
  const [isPlannerWorking, setPlannerWorking] = useState(false)
  const [forceReloadCalendar, setForceReloadCalendar] = useState(false)
  // Story 153, CU9 (S100-S104): a Gantt job drop, Publish, Reset, Save, Activate, Plan or Delete can
  // all leave `selectedFile.id` and `appliedPlannerFilters` unchanged, so they cannot rely on
  // `usePlanMetrics`'s own dependency-based refetch (which only watches the plan id and the global
  // filters). This counter is the single extra signal the ribbon and the load-capacity block both
  // watch to call their own (already debounced, S104) `reload()` after any of those succeed, without
  // reloading the screen or the Gantt (RN59/I20).
  const [planMetricsVersion, setPlanMetricsVersion] = useState(0)
  const bumpPlanMetricsVersion = useCallback(() => {
    setPlanMetricsVersion((version) => version + 1)
  }, [])

  // Story 175 -- the seams the three construction slices share (contract §7):
  // - `machineDetailDeviceId`: the machine whose detail drawer is open; null means closed (J1). A Gantt
  //   row label or a sibling of the drawer REPLACES it (RN7); opening or closing it touches nothing else
  //   of the screen (RN6).
  // - `selectedFabricationOrderId`: the ONE selected fabrication order of the Planner (RN45/INV-H), null
  //   when none. The drawer toggles it today (J1); the details table and the Gantt border read and
  //   write it through `toggleSelectedFabricationOrder` in J2/J3.
  // - `eligibleDeviceIds`: Sun's list of machines that may open a detail (RN4), read ONCE per Planner
  //   load (S85): neither a plan change nor a filter change asks for it again.
  const [machineDetailDeviceId, setMachineDetailDeviceId] = useState(null)
  const closeMachineDetail = useCallback(() => setMachineDetailDeviceId(null), [])
  const [selectedFabricationOrderId, setSelectedFabricationOrderId] = useState(null)
  const toggleSelectedFabricationOrder = useCallback((fabricationOrderId) => {
    setSelectedFabricationOrderId((selectedId) => (selectedId === fabricationOrderId ? null : fabricationOrderId))
  }, [])
  const [eligibleDeviceIds, setEligibleDeviceIds] = useState([])
  // Story 175, RN45 (S146): the selection only lives within the plan that is on screen. Changing plan drops it
  // here; a global filter or a write that takes the order out of the plan is reported by the Gantt, which
  // watches its own `/deliveries` answer (`onSelectedFabricationOrderGone`). A write that keeps the same plan
  // id (save, drag) does not drop it.
  const clearSelectedFabricationOrder = useCallback(() => setSelectedFabricationOrderId(null), [])
  const selectedPlanificationId = selectedFile?.id
  useEffect(() => {
    clearSelectedFabricationOrder()
  }, [selectedPlanificationId, clearSelectedFabricationOrder])
  // Story 175, RN8 bis (S167): deleting the plan that is on screen closes the machine detail (there is no plan
  // left to describe); the Planner then does what it always did after a deletion, with no new notice. Deleting
  // another plan leaves the drawer to follow the plan change as any other (RN8).
  const selectedPlanificationIdRef = useRef(selectedPlanificationId)
  selectedPlanificationIdRef.current = selectedPlanificationId
  const handlePlanDeleted = useCallback((deletedPlanificationId) => {
    if (deletedPlanificationId === selectedPlanificationIdRef.current) {
      setMachineDetailDeviceId(null)
    }
  }, [])

  const [toolGroups, setToolGroups] = useState(null)
  const [plannerFilterOptions, setPlannerFilterOptions] = useState({
    fabricationOrders: [],
    partReferences: [],
    orders: [],
    clients: []
  })
  const [draftPlannerFilters, setDraftPlannerFilters] = useState(emptyPlannerFilters)
  const [appliedPlannerFilters, setAppliedPlannerFilters] = useState(emptyPlannerFilters)
  const [AlertElement, showAlert, dismissAlert] = AlertUI()
  const { t_ } = useSmartTranslation()
  const [plannerProgress, setPlannerProgress] = useState(null)
  const [eventInfoOnClick, setEventInfoOnClick] = useState(null)
  const isUserPlannerAuthorized = usePlannerAuthorization()
  const isUserPlannerAuthorizedRef = useRef(isUserPlannerAuthorized)
  const [orderEdited, setOrderEdited] = useState(false)
  // The block whose press is about to become a drag. While it is set EVERY body is hidden, so the
  // user drags a short stack of headers, with a ghost of the pressed header under the pointer -- see
  // PlannerBlockAccordion. Nobody is reordering while the key is null.
  const [reorderingBlockKey, setReorderingBlockKey] = useState(null)
  const [reorderingMinWidth, setReorderingMinWidth] = useState(0)
  // How far the fold lifted the pressed block (px). A ref, not state: it is read once, when dnd-kit
  // renders the ghost at drag start, and nothing needs to re-render when it changes.
  const foldShiftRef = useRef(0)
  const beginReordering = useCallback((blockKey, listMinWidth) => {
    setReorderingMinWidth(listMinWidth)
    setReorderingBlockKey(blockKey)
  }, [])
  const holdGhostUnderPointer = useCallback((shift) => { foldShiftRef.current = shift }, [])
  const endReordering = useCallback(() => {
    foldShiftRef.current = 0
    setReorderingBlockKey(null)
    setReorderingMinWidth(0)
  }, [])
  const onEventClick = useCallback((eventInfo) => {
    if (selectedFile?.fileName === 'Temp' || selectedFile?.fileName === 'Current') {
      setEventInfoOnClick(eventInfo)
    }
  }, [selectedFile?.fileName])

  /** @type {[Planification[], React.Dispatch<React.SetStateAction<Planification[]>>]} */
  const [publishedPlans, setPublishedPlans] = useState([])

  // Load "Current" plan if nothing more selected
  if (Object.keys(selectedFile).length === 0 && publishedPlans.length > 0) {
    const file = publishedPlans.find((item) => item.current)
    setSelectedFile(file)
  }

  useEffect(() => {
    setPlannerProgress(null)
  }, [isPlannerWorking])

  useEffect(() => {
    const loadToolGroups = async () => {
      if (!isUserPlannerAuthorizedRef.current('PLANIFICATION_TOOLS')) {
        return []
      }
      const groups = await apiGet('toolGroupsBasicData')
      const filteredGroups = groups.filter((toolGroup) => toolGroup.code === 'tgr_001' || toolGroup.code === 'tgr_002').sort((a, b) => {
        if (a.code < b.code) return -1
        if (a.code > b.code) return 1
        return 0
      })
      setToolGroups(filteredGroups)
    }
    loadToolGroups()
  }, [])

  // S85 -- once per Planner load, and only for a Planner user (S112: without PLANNER/AI_PLANNER there
  // is no entry to the detail at all). A failed read leaves every row as a plain label (P3).
  useEffect(() => {
    if (!isUserPlannerAuthorizedRef.current('PLANNER')) {
      return
    }
    apiGet('planMetrics/eligibleDevices')
      .then((response) => setEligibleDeviceIds(Array.isArray(response?.deviceIds) ? response.deviceIds : []))
      .catch(() => setEligibleDeviceIds([]))
  }, [])

  // Story 221 (RN17): Part Reference and Client keep the whole catalogue, read once per Planner load. The update
  // is functional because the plan's own options (below) may already have landed.
  useEffect(() => {
    const loadCatalogueFilterOptions = async () => {
      const [partReferencesResponse, clientsResponse] = await Promise.all([
        apiGet('partReferencesShortData').catch(() => []),
        apiGet('clientsBasicData').catch(() => [])
      ])
      setPlannerFilterOptions((options) => ({
        ...options,
        partReferences: Array.isArray(partReferencesResponse) ? partReferencesResponse : [],
        clients: Array.isArray(clientsResponse) ? clientsResponse : []
      }))
    }
    loadCatalogueFilterOptions()
  }, [])

  // Story 221 (RN9): another plan empties Fabrication Order and Order until its own options arrive -- never the
  // previous plan's, never a catalogue. An edit of the same plan (version bump) only goes through the effect
  // below, so the selectors keep what they had meanwhile (RN13b).
  useEffect(() => {
    setPlannerFilterOptions(withoutPlanOptions)
  }, [selectedPlanificationId])

  // Story 221 (RN8, RN10-RN14, D8, D9): Fabrication Order and Order offer what the plan on screen has planned
  // (contract C1), read again on every plan change and on every edit of the plan -- `planMetricsVersion`, the
  // same signal that reloads the ribbon and the load-capacity block (Search and Reset bump it too). Only the
  // latest request paints: the cleanup of a superseded run silences its answer. A good answer also clears, in
  // the draft and in the applied filters, a value that is no longer offered; `reconcilePlannerFilters` returns
  // the same object otherwise, so nothing reloads, and `reloadCalendar` is never called from here. A failed or
  // shapeless answer empties the two selectors and leaves every filter as it was.
  useEffect(() => {
    if (!selectedPlanificationId) {
      return
    }
    let latest = true
    apiGet('planner/filterOptions/' + selectedPlanificationId)
      .then((response) => {
        if (!latest) {
          return
        }
        if (!Array.isArray(response?.fabricationOrders) || !Array.isArray(response?.orders)) {
          setPlannerFilterOptions(withoutPlanOptions)
          return
        }
        const planOptions = { fabricationOrders: response.fabricationOrders, orders: response.orders }
        setPlannerFilterOptions((options) => ({ ...options, ...planOptions }))
        setDraftPlannerFilters((filters) => reconcilePlannerFilters(filters, planOptions))
        setAppliedPlannerFilters((filters) => reconcilePlannerFilters(filters, planOptions))
      })
      .catch(() => {
        if (latest) {
          setPlannerFilterOptions(withoutPlanOptions)
        }
      })
    return () => {
      latest = false
    }
  }, [selectedPlanificationId, planMetricsVersion])

  const loadPublishedPlans = useCallback(async () => {
    const publishedPlans = await apiGet('planner/getCurrentPlanifications')
    if (publishedPlans && selectedFile && selectedFile.fileName) {
      const newSelectedFile = publishedPlans.find((item) => item.fileName === selectedFile.fileName)
      if (newSelectedFile && newSelectedFile.id !== selectedFile.id) {
        setSelectedFile(newSelectedFile)
      }
    }
    setPublishedPlans(publishedPlans)
  }, [selectedFile])

  useEffect(() => {
    loadPublishedPlans()
  }, [loadPublishedPlans])

  useEffect(() => {
    let mounted = true
    const interval = setInterval(async () => {
      const response = await apiGet('planner/working')
      if (mounted) {
        // Check if we were loading previously and it finished
        if (!response.working && isPlannerWorking) {
          setForceReloadCalendar(!forceReloadCalendar)
          // S100 ("pulsa Planificar"): the AI planner finishes asynchronously, off this same poller,
          // once `working` flips back to false.
          bumpPlanMetricsVersion()
        }

        setPlannerProgress(response)

        if (isPlannerWorking !== response.working) {
          if (isPlannerWorking && !response.working) {
            setForceReloadCalendar(!forceReloadCalendar)
            bumpPlanMetricsVersion()
            // Load published plans in case IDs have changed
            loadPublishedPlans()
          }
          setPlannerWorking(response.working)
        }
      }
      if (response.statusCode > 299) {
        if (response.statusCode >= 400 && response.statusCode < 500) {
          showAlert({ severity: 'warning', title: response.message })
        } else {
          showAlert({ severity: 'error', title: response.message })
        }
      }
    }, 1000)
    return () => {
      mounted = false
      clearInterval(interval)
    }
  }, [isPlannerWorking, forceReloadCalendar, showAlert, loadPublishedPlans, bumpPlanMetricsVersion])

  const planificationToolColumns = [{
    name: t_('Code'),
    field: 'code'
  },
  {
    name: t_('Name'),
    field: 'name'
  },
  {
    name: t_('Description'),
    field: 'description'
  },
  {
    name: t_('Tool type'),
    field: 'toolType.name',
    type: 'select'
  }
  ]

  const millingParameters = [
    {
      name: 'D',
      field: 'd'
    },
    {
      name: 'Rc',
      field: 'rc'
    },
    {
      name: 'TL',
      field: 'tl'
    },
    {
      name: 'CL',
      field: 'cl'
    },
    {
      name: 'Z',
      field: 'z'
    }
  ]
  const turningParameters = [
    {
      name: 'XL',
      field: 'xl'
    },
    {
      name: 'YL',
      field: 'yl'
    },
    {
      name: 'ZL',
      field: 'zl'
    }
  ]
  const toolLifeParameterColumns = [{
    name: t_('Total Life'),
    field: 'totalLife'
  },
  {
    name: t_('Life warning'),
    field: 'lifeWarning'
  },
  {
    name: t_('Holder'),
    field: 'holder.name'
  },
  {
    name: t_('Material type'),
    field: 'materialType.name'
  },
  {
    name: t_('Necessary life'),
    field: 'lifeNeeded'
  }]

  const reloadCalendar = useCallback(() => {
    setForceReloadCalendar(!forceReloadCalendar)
    // S100 ("pulsa Buscar"/"pulsa Restablecer"): `appliedPlannerFilters` already retriggers
    // `usePlanMetrics` on its own when it actually changes, but "Restablecer" from an already-empty
    // state would otherwise leave the ribbon/load-capacity block silently stale.
    bumpPlanMetricsVersion()
  }, [forceReloadCalendar, bumpPlanMetricsVersion])

  const dataFetch = async (pagination, toolGroupId) => {
    return apiGet('planificationTools/' + selectedFile?.id + '/' + toolGroupId, pagination)
  }

  const fabricationOrderOptions = buildOptionsMap(plannerFilterOptions.fabricationOrders)
  const partReferenceOptions = buildOptionsMap(plannerFilterOptions.partReferences)
  const orderOptions = buildOptionsMap(plannerFilterOptions.orders)
  const clientOptions = buildOptionsMap(plannerFilterOptions.clients)

  const getSelectedFilterLabel = (optionsMap, id) => {
    if (!id) {
      return null
    }
    return Array.from(optionsMap.entries()).find(([, item]) => item.id === id)?.[0] ?? null
  }

  // S90 -- the visible names of the APPLIED global filters, exactly as their selectors show them
  // ("FO 0417", "Client ACME"): Jupiter already has them, so nothing is asked of Sun.
  const optionsMapByFilter = {
    fabricationOrderId: fabricationOrderOptions,
    partReferenceId: partReferenceOptions,
    orderId: orderOptions,
    clientId: clientOptions
  }
  const appliedFilterLabels = FILTER_CAPTION_KEYS
    .map(([field, captionKey]) => {
      const label = getSelectedFilterLabel(optionsMapByFilter[field], appliedPlannerFilters[field])
      return label ? `${t_(captionKey)} ${label}` : ''
    })
    .filter((caption) => caption !== '')

  const updateDraftPlannerFilter = (field, optionsMap, selectedLabel) => {
    setDraftPlannerFilters({
      ...draftPlannerFilters,
      [field]: selectedLabel ? optionsMap.get(selectedLabel)?.id ?? null : null
    })
  }

  const applyPlannerFilters = () => {
    setAppliedPlannerFilters(draftPlannerFilters)
    reloadCalendar()
  }

  const resetPlannerFilters = () => {
    const nextFilters = createEmptyPlannerFilters()
    setDraftPlannerFilters(nextFilters)
    setAppliedPlannerFilters(nextFilters)
    reloadCalendar()
  }

  // Story 153, CU10/CU11: order and collapsed state of the four accordion blocks, persisted per user.
  const [confirmResetBlocksOpen, setConfirmResetBlocksOpen] = useState(false)
  const handleBlockLayoutSaveError = useCallback(() => {
    showAlert({ severity: 'warning', title: t_('The block order could not be saved') })
  }, [showAlert, t_])
  const {
    items: plannerBlocks,
    isLoaded: plannerBlocksLoaded,
    onDragEnd: onPlannerBlocksDragEnd,
    onBlockExpandedChange: onPlannerBlockExpandedChange,
    resetToDefault: resetPlannerBlocksToDefault
  } = usePlannerBlocksLayout({ onSaveError: handleBlockLayoutSaveError })

  const openConfirmResetBlocks = () => setConfirmResetBlocksOpen(true)
  const cancelResetBlocks = () => setConfirmResetBlocksOpen(false)
  const confirmResetBlocks = () => {
    resetPlannerBlocksToDefault()
    setConfirmResetBlocksOpen(false)
    showAlert({ severity: 'success', title: t_('Block order restored') })
  }

  const blockTitleByKey = {
    TIMELINE: t_('Timeline'),
    LOAD_CAPACITY: t_('Load and capacity'),
    DETAILS_TABLE: t_('Details table'),
    PLANNING_TOOLS: t_('Planification tools')
  }

  const isBlockDisabled = (key) => {
    if (key === 'DETAILS_TABLE') {
      return !isUserPlannerAuthorized('DETAILS_TABLE')
    }
    if (key === 'PLANNING_TOOLS') {
      return !isUserPlannerAuthorized('PLANIFICATION_TOOLS')
    }
    return false
  }

  const renderBlockContent = (key) => {
    switch (key) {
      case 'TIMELINE':
        return (
          <>
            <Stack direction='row' gap={2} alignItems='center' flexWrap='wrap' sx={{ marginBottom: '1rem' }}>
              <SmartSelect
                value={getSelectedFilterLabel(fabricationOrderOptions, draftPlannerFilters.fabricationOrderId)}
                selectableOptions={Array.from(fabricationOrderOptions.keys())}
                onChange={(newValue) => updateDraftPlannerFilter('fabricationOrderId', fabricationOrderOptions, newValue)}
                label={t_('Fabrication Order')}
                width='16rem'
                size='small'
                disableClearable={false}
                autoSelectFirst={false}
              />
              <SmartSelect
                value={getSelectedFilterLabel(partReferenceOptions, draftPlannerFilters.partReferenceId)}
                selectableOptions={Array.from(partReferenceOptions.keys())}
                onChange={(newValue) => updateDraftPlannerFilter('partReferenceId', partReferenceOptions, newValue)}
                label={t_('Part Reference')}
                width='16rem'
                size='small'
                disableClearable={false}
                autoSelectFirst={false}
              />
              <SmartSelect
                value={getSelectedFilterLabel(orderOptions, draftPlannerFilters.orderId)}
                selectableOptions={Array.from(orderOptions.keys())}
                onChange={(newValue) => updateDraftPlannerFilter('orderId', orderOptions, newValue)}
                label={t_('Order')}
                width='16rem'
                size='small'
                disableClearable={false}
                autoSelectFirst={false}
              />
              <SmartSelect
                value={getSelectedFilterLabel(clientOptions, draftPlannerFilters.clientId)}
                selectableOptions={Array.from(clientOptions.keys())}
                onChange={(newValue) => updateDraftPlannerFilter('clientId', clientOptions, newValue)}
                label={t_('Client')}
                width='16rem'
                size='small'
                disableClearable={false}
                autoSelectFirst={false}
              />
              <Button variant='outlined' endIcon={<Search />} onClick={applyPlannerFilters}>
                {t_('Search')}
              </Button>
              <Button variant='outlined' endIcon={<RestartAlt />} onClick={resetPlannerFilters}>
                {t_('Reset filters')}
              </Button>
            </Stack>
            <Timeline
              selectedFile={selectedFile}
              setSelectedFile={(file) => setSelectedFile(file)}
              isPlannerWorking={isPlannerWorking}
              setIsPlannerWorking={(working) => setPlannerWorking(working)}
              forceReloadCalendar={forceReloadCalendar}
              reloadCalendar={reloadCalendar}
              publishedPlans={publishedPlans}
              loadPublishedPlans={loadPublishedPlans}
              AlertElement={AlertElement}
              showAlert={showAlert}
              dismissAlert={dismissAlert}
              plannerProgress={plannerProgress}
              eventInfoOnClick={eventInfoOnClick}
              onEventClick={onEventClick}
              setOrderEdited={setOrderEdited}
              orderEdited={orderEdited}
              plannerFilters={appliedPlannerFilters}
              metricsVersion={planMetricsVersion}
              bumpPlanMetricsVersion={bumpPlanMetricsVersion}
              eligibleDeviceIds={eligibleDeviceIds}
              onMachineClick={setMachineDetailDeviceId}
              selectedFabricationOrderId={selectedFabricationOrderId}
              onSelectedFabricationOrderGone={clearSelectedFabricationOrder}
              onPlanDeleted={handlePlanDeleted}
            />
          </>
        )
      case 'DETAILS_TABLE':
        return (
          <PlannerDetailsPanel
            selectedFile={selectedFile}
            setEventInfoOnClick={setEventInfoOnClick}
            reloadCalendar={reloadCalendar}
            showAlert={showAlert}
            setOrderEdited={setOrderEdited}
            plannerFilters={appliedPlannerFilters}
            metricsVersion={planMetricsVersion}
            selectedFabricationOrderId={selectedFabricationOrderId}
            onFabricationOrderClick={toggleSelectedFabricationOrder}
          />
        )
      case 'PLANNING_TOOLS':
        return toolGroups
          ? toolGroups.map(element => {
            const parameterColumns = element.name === 'Milling' ? millingParameters : turningParameters
            return (
              <DefaultAccordion title={t_(element.name)} defaultExpanded={false} key={element.id}>
                {/* The generic table does not scroll sideways by itself: here it does, inside its block. */}
                <Box sx={{ overflowX: 'auto' }}>
                  <SmartMateriaUITable
                    title=''
                    columns={[...planificationToolColumns, ...parameterColumns, ...toolLifeParameterColumns]}
                    dataFetch={(pagination) => dataFetch(pagination, element.id)}
                    pageable
                  />
                </Box>
              </DefaultAccordion>
            )
          })
          : null
      // LOAD_CAPACITY (CU8): this accordion only owns the block's position and collapsed state --
      // everything else lives in `LoadCapacityBlock.jsx` itself.
      case 'LOAD_CAPACITY':
        return (
          <LoadCapacityBlock
            planificationId={selectedFile?.id}
            plannerFilters={appliedPlannerFilters}
            metricsVersion={planMetricsVersion}
          />
        )
      default:
        // `DefaultAccordion` requires a non-null `children`; an empty fragment is a valid node.
        return <></>
    }
  }

  // The ghost dnd-kit draws under the pointer: the block's header alone, as the folded block looks,
  // pushed down by what the fold lifted the block, so it appears where the finger is. dnd-kit
  // measures it offset and all, so the drop target is looked for where the ghost is drawn.
  const renderBlockGhost = (item) => (
    <div data-testid='planner-block-ghost' style={{ transform: 'translateY(' + foldShiftRef.current + 'px)' }}>
      <DefaultAccordion title={blockTitleByKey[item.key]} defaultExpanded={false} headerSx={{ cursor: 'grabbing', userSelect: 'none' }}>
        <div />
      </DefaultAccordion>
    </div>
  )

  const plannerBlockLayoutContextValue = {
    blockTitleByKey,
    isBlockDisabled,
    renderBlockContent,
    onBlockExpandedChange: onPlannerBlockExpandedChange,
    isReordering: reorderingBlockKey !== null,
    beginReordering,
    holdGhostUnderPointer,
    endReordering
  }

  return (
    <>
      {plannerBlocksLoaded && (
        <PlannerBlockLayoutContext.Provider value={plannerBlockLayoutContextValue}>
          <Box
            data-testid='planner-blocks-list'
            // While reordering: the width the list had before the fold, and no text selection -- a
            // mouse held down while moving would otherwise paint a selection across the page.
            // `contain: inline-size` (story 177, as Monitoring's `byGroup.css` does): the blocks' content no longer drives
            // the min-content width that `MainContainer` gives the page, so opening the Timeline (the Gantt) or the
            // details table does not make the Planner wider than the screen; what is wider scrolls inside its own block.
            sx={{ contain: 'inline-size', ...(reorderingBlockKey !== null ? { minWidth: reorderingMinWidth, userSelect: 'none' } : {}) }}
          >
            <SmartSortableContainer
              itemList={plannerBlocks}
              itemId='key'
              itemOrder='position'
              renderItem={PlannerBlockAccordion}
              onSortChanged={onPlannerBlocksDragEnd}
              dragHandle
              autoScroll={false}
              restoreFocus={false}
              keyboardSensor={false}
              renderOverlay={renderBlockGhost}
            />
          </Box>
        </PlannerBlockLayoutContext.Provider>
      )}

      {/* The PO wanted "Restore default order" as a floating button at the bottom, like Monitoring's "Arrange": a
          bar that sticks to the bottom of the screen, as wide as the page, and only the pill takes clicks. */}
      <Box data-testid='planner-restore-order-bar' sx={{ position: 'sticky', bottom: 0, zIndex: 20, marginTop: '1rem', pointerEvents: 'none' }}>
        <Stack direction='row' justifyContent='flex-end' sx={{ paddingY: '10px' }}>
          <ButtonBase onClick={openConfirmResetBlocks} sx={restoreOrderPillSx} data-testid='planner-restore-order'>
            <RestartAlt sx={{ fontSize: 16 }} />
            <span>{t_('Restore default order')}</span>
          </ButtonBase>
        </Stack>
      </Box>

      <MachineDetailDrawer
        planificationId={selectedFile?.id}
        deviceId={machineDetailDeviceId}
        plannerFilters={appliedPlannerFilters}
        filterLabels={appliedFilterLabels}
        metricsVersion={planMetricsVersion}
        selectedFabricationOrderId={selectedFabricationOrderId}
        onFabricationOrderClick={toggleSelectedFabricationOrder}
        onMachineClick={setMachineDetailDeviceId}
        onClose={closeMachineDetail}
      />

      <Dialog open={confirmResetBlocksOpen} onClose={cancelResetBlocks}>
        <DialogTitle>{t_('Restore default order')}</DialogTitle>
        <DialogActions>
          <Button onClick={cancelResetBlocks}>{t_('Cancel')}</Button>
          <Button onClick={confirmResetBlocks} autoFocus>{t_('Confirm')}</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default Planner
